import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { generateReceiptHeader, getCollegeInfo } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';
applyPlugin(jsPDF);

/**
 * Generate Salary Slip PDF
 */
export const generateSalarySlip = async (salaryData, companyInfo = null) => {
  const doc = new jsPDF();
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 14;

  // Get college info - prefer parameter over localStorage
  let collegeInfo = companyInfo;
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('generateSalarySlip: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }
  let yPos = await generateReceiptHeader(doc, collegeInfo, 'SALARY SLIP');

  // Month and Employee ID
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Month:', margin, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(salaryData.month_display || salaryData.month || 'N/A', margin + 20, yPos);
  
  doc.setFont(resolvedFont, 'bold');
  doc.text('Employee ID:', 120, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(salaryData.employee_id || salaryData.staff_id || 'N/A', 155, yPos);

  yPos += 12;

  // Employee Information
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text('Employee Information', margin, yPos);
  doc.setTextColor(0, 0, 0);
  
  yPos += 2;
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  const employeeInfo = [
    ['Employee Name', salaryData.staff_name || salaryData.employee_name || 'N/A'],
    ['Department', salaryData.department || 'N/A'],
    ['Designation', salaryData.designation || 'N/A'],
    ['Date of Joining', salaryData.joining_date ? formatDate(salaryData.joining_date) : 'N/A'],
    ['Bank Account', salaryData.bank_account || salaryData.account_number || 'N/A'],
    ['PAN Number', salaryData.pan_number || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: employeeInfo,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold', textColor: [80, 80, 80] },
      1: { cellWidth: 140, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 8;

  // Earnings and Deductions side by side
  const colWidth = (pageWidth - margin * 2 - 10) / 2;

  // Earnings
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.setFillColor(230, 247, 230);
  doc.rect(margin, yPos, colWidth, 8, 'F');
  doc.setTextColor(0, 100, 0);
  doc.text('EARNINGS', margin + 5, yPos + 5.5);
  
  // Deductions
  doc.setFillColor(255, 235, 235);
  doc.rect(margin + colWidth + 10, yPos, colWidth, 8, 'F');
  doc.setTextColor(150, 0, 0);
  doc.text('DEDUCTIONS', margin + colWidth + 15, yPos + 5.5);
  
  doc.setTextColor(0, 0, 0);
  yPos += 10;

  // Earnings data
  const earnings = salaryData.earnings || [
    { name: 'Basic Salary', amount: salaryData.basic_salary || 0 },
    { name: 'HRA', amount: salaryData.hra || 0 },
    { name: 'DA', amount: salaryData.da || 0 },
    { name: 'TA', amount: salaryData.ta || 0 },
    { name: 'Special Allowance', amount: salaryData.special_allowance || 0 },
    { name: 'Other Allowance', amount: salaryData.other_allowance || 0 }
  ];

  const deductions = salaryData.deductions || [
    { name: 'PF', amount: salaryData.pf || 0 },
    { name: 'ESI', amount: salaryData.esi || 0 },
    { name: 'Professional Tax', amount: salaryData.professional_tax || 0 },
    { name: 'TDS', amount: salaryData.tds || 0 },
    { name: 'Loan Recovery', amount: salaryData.loan_recovery || 0 },
    { name: 'Other Deduction', amount: salaryData.other_deduction || 0 }
  ];

  // Draw earnings
  doc.setFontSize(9);
  let earningsY = yPos;
  let totalEarnings = 0;
  earnings.forEach(item => {
    if (item.amount > 0) {
      doc.setFont(resolvedFont, 'normal');
      doc.text(item.name, margin + 2, earningsY);
      doc.text(`${CURRENCY} ${formatINR(item.amount)}`, margin + colWidth - 5, earningsY, { align: 'right' });
      totalEarnings += item.amount;
      earningsY += 6;
    }
  });

  // Draw deductions
  let deductionsY = yPos;
  let totalDeductions = 0;
  deductions.forEach(item => {
    if (item.amount > 0) {
      doc.setFont(resolvedFont, 'normal');
      doc.text(item.name, margin + colWidth + 12, deductionsY);
      doc.text(`${CURRENCY} ${formatINR(item.amount)}`, pageWidth - margin - 2, deductionsY, { align: 'right' });
      totalDeductions += item.amount;
      deductionsY += 6;
    }
  });

  yPos = Math.max(earningsY, deductionsY) + 5;

  // Totals row
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, margin + colWidth, yPos);
  doc.line(margin + colWidth + 10, yPos, pageWidth - margin, yPos);
  yPos += 6;

  doc.setFont(resolvedFont, 'bold');
  doc.text('Total Earnings', margin + 2, yPos);
  doc.text(`${CURRENCY} ${formatINR((salaryData.gross_salary || totalEarnings))}`, margin + colWidth - 5, yPos, { align: 'right' });
  
  doc.text('Total Deductions', margin + colWidth + 12, yPos);
  doc.text(`${CURRENCY} ${formatINR((salaryData.total_deductions || totalDeductions))}`, pageWidth - margin - 2, yPos, { align: 'right' });

  yPos += 15;

  // Net Salary Box
  const netSalary = salaryData.net_salary || (totalEarnings - totalDeductions);
  doc.setFillColor(0, 51, 102);
  doc.rect(margin, yPos, pageWidth - margin * 2, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('NET SALARY', margin + 10, yPos + 10);
  doc.text(`${CURRENCY} ${formatINR(netSalary)}`, pageWidth - margin - 10, yPos + 10, { align: 'right' });
  
  doc.setTextColor(0, 0, 0);
  yPos += 25;

  // Amount in Words
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Amount in Words:', margin, yPos);
  doc.setFont(resolvedFont, 'normal');
  const amountInWords = numberToWords(netSalary);
  doc.text(amountInWords + ' Only', margin + 38, yPos);

  // Footer - Signatures
  const footerY = pageHeight - 40;
  
  doc.setLineWidth(0.3);
  doc.line(margin, footerY, margin + 50, footerY);
  doc.line(pageWidth - margin - 50, footerY, pageWidth - margin, footerY);
  
  doc.setFontSize(8);
  doc.setFont(resolvedFont, 'normal');
  doc.text('Employee Signature', margin + 25, footerY + 5, { align: 'center' });
  doc.text('Authorized Signatory', pageWidth - margin - 25, footerY + 5, { align: 'center' });

  // Disclaimer
  doc.setFontSize(7);
  doc.setFont(resolvedFont, 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('This is a computer-generated salary slip and does not require a signature.', pageWidth / 2, pageHeight - 15, { align: 'center' });
  doc.text('For any discrepancy, please contact the HR department within 7 days.', pageWidth / 2, pageHeight - 10, { align: 'center' });

  // Save the PDF
  const fileName = `Salary_Slip_${salaryData.staff_name || 'Employee'}_${salaryData.month || Date.now()}.pdf`;
  doc.save(fileName);
  
  return doc;
};

// Helper function to convert number to words
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  if (num < 0) return 'Minus ' + numberToWords(-num);
  
  let words = '';
  
  if (Math.floor(num / 10000000) > 0) {
    words += numberToWords(Math.floor(num / 10000000)) + ' Crore ';
    num %= 10000000;
  }
  
  if (Math.floor(num / 100000) > 0) {
    words += numberToWords(Math.floor(num / 100000)) + ' Lakh ';
    num %= 100000;
  }
  
  if (Math.floor(num / 1000) > 0) {
    words += numberToWords(Math.floor(num / 1000)) + ' Thousand ';
    num %= 1000;
  }
  
  if (Math.floor(num / 100) > 0) {
    words += numberToWords(Math.floor(num / 100)) + ' Hundred ';
    num %= 100;
  }
  
  if (num > 0) {
    if (num < 20) {
      words += ones[num];
    } else {
      words += tens[Math.floor(num / 10)];
      if (num % 10 > 0) {
        words += ' ' + ones[num % 10];
      }
    }
  }
  
  return words.trim() + ' Rupees';
};

export default generateSalarySlip;
